/**
 * Generates a full brand color palette (25–900) from a single hex color.
 * The input hex is used as the 500 shade, and all other shades are derived
 * by adjusting lightness and saturation in HSL space.
 */

function hexToHsl(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) throw new Error(`Invalid hex color: ${hex}`);

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / delta + 2) * 60;
    else h = ((r - g) / delta + 4) * 60;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Generates a brand color palette from a single hex color.
 *
 * @param hex - The brand color hex (used as the 500 shade). e.g. "#2563eb"
 * @returns Record mapping shade names (25, 50, 100-900) to hex values
 *
 * The algorithm uses lightness multipliers calibrated against professional
 * design system palettes. Lighter shades also reduce saturation for a
 * more natural appearance.
 */
export function generateBrandPalette(hex: string): Record<string, string> {
  const [h, s, l] = hexToHsl(hex);

  return {
    '25': hslToHex(h, clamp(s * 0.35, 5, 100), clamp(l * 1.46, 0, 99)),
    '50': hslToHex(h, clamp(s * 0.4, 5, 100), clamp(l * 1.43, 0, 98)),
    '100': hslToHex(h, clamp(s * 0.5, 5, 100), clamp(l * 1.37, 0, 95)),
    '200': hslToHex(h, clamp(s * 0.6, 5, 100), clamp(l * 1.3, 0, 92)),
    '300': hslToHex(h, clamp(s * 0.7, 5, 100), clamp(l * 1.21, 0, 87)),
    '400': hslToHex(h, clamp(s * 0.85, 5, 100), clamp(l * 1.12, 0, 80)),
    '500': hex,
    '600': hslToHex(h, clamp(s, 5, 100), clamp(l * 0.81, 10, 100)),
    '700': hslToHex(h, clamp(s, 5, 100), clamp(l * 0.62, 8, 100)),
    '800': hslToHex(h, clamp(s, 5, 100), clamp(l * 0.47, 6, 100)),
    '900': hslToHex(h, clamp(s, 5, 100), clamp(l * 0.3, 5, 100)),
  };
}
